// MakeVocalsSoundFullerLearning.jsx
// Route suggestion: /learning/how-to-make-vocals-sound-fuller

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";
import ArticleSchemaMarkup from "./ArticleSchemaMarkup.jsx";

export default function MakeVocalsSoundFullerLearning() {
  const { style } = useStyle();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";

  const quickFacts = [
    {
      title: "What fullness means",
      text: "A full vocal has body, presence, steady level, harmonic detail, and enough space around it to feel finished without sounding muddy.",
      icon: <MicRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where fullness lives",
      text: "Vocal fullness often comes from the low mids, controlled dynamics, saturation, subtle ambience, and a clear relationship with the rest of the mix.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Best first move",
      text: "Start with level balance and cleanup before adding warmth or effects. A vocal that is too quiet or uneven will rarely feel full.",
      icon: <TuneRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const fullnessAreas = [
    {
      range: "100–180 Hz",
      title: "Weight and chest",
      description:
        "This area can add size to some voices, especially deeper vocals, but too much can make the vocal boomy or cloudy.",
    },
    {
      range: "180–350 Hz",
      title: "Body and warmth",
      description:
        "This is a major area for vocal fullness. Too little can make the vocal thin; too much can make it boxy or muddy.",
    },
    {
      range: "700 Hz–1.5 kHz",
      title: "Forwardness",
      description:
        "This range helps the vocal feel present and intelligible. Too much can sound nasal or honky.",
    },
    {
      range: "2–5 kHz",
      title: "Presence and clarity",
      description:
        "This area helps words cut through the mix. It can make a vocal feel more finished, but too much becomes harsh or fatiguing.",
    },
    {
      range: "6–10 kHz",
      title: "Detail and sibilance",
      description:
        "This range carries breath, consonants, and brightness. It can add life, but it can also exaggerate S sounds.",
    },
    {
      range: "10 kHz+",
      title: "Air",
      description:
        "Air can make a vocal feel open and expensive, but it should not be used to compensate for missing body or poor level balance.",
    },
  ];

  const techniques = [
    {
      title: "Get the vocal level steady first",
      description:
        "A vocal often sounds thin because words disappear or jump forward unevenly. Before adding EQ or saturation, use clip gain, automation, auto leveling, or compression to keep the vocal consistently present.",
    },
    {
      title: "Clean up low-end rumble",
      description:
        "A careful high-pass filter removes plosives, mic stand rumble, and room noise that do not contribute to vocal fullness. The goal is to remove clutter without stripping away chest and body.",
    },
    {
      title: "Add body with broad, gentle moves",
      description:
        "If the vocal is thin, look around the low mids rather than immediately boosting high frequencies. Small, broad moves often sound more natural than narrow boosts.",
    },
    {
      title: "Use saturation for harmonic density",
      description:
        "Saturation can make a vocal feel richer and easier to hear by adding harmonics. This can create fullness without simply turning up the vocal or adding too much low-mid EQ.",
    },
    {
      title: "Control harshness before adding air",
      description:
        "A bright vocal can still feel thin if the upper mids are harsh. Smooth harsh spots before adding air so the vocal opens up without becoming sharp.",
    },
    {
      title: "Use compression to hold the vocal forward",
      description:
        "Compression can help the vocal stay connected to the mix. The goal is not to flatten every phrase, but to keep the performance stable and emotionally present.",
    },
    {
      title: "Add space without washing it out",
      description:
        "Short reverbs, plates, rooms, delays, and subtle ambience can make a vocal feel larger. Too much space pushes the vocal back and can make it feel less full.",
    },
  ];

  const processingTools = [
    {
      title: "EQ",
      description:
        "Use EQ to remove rumble, reduce boxiness, shape body, control harshness, and add air. EQ is important, but it cannot create harmonic richness by itself.",
    },
    {
      title: "Compression",
      description:
        "Compression keeps words more consistent and helps the vocal remain present. It can also add density when used with the right attack and release settings.",
    },
    {
      title: "Auto leveling",
      description:
        "Auto leveling or gain riding helps smooth volume changes before heavy compression. This can make the vocal feel more natural and easier to mix.",
    },
    {
      title: "Saturation",
      description:
        "Saturation adds harmonic detail and density. It can make a vocal sound fuller, closer, and more finished without relying only on EQ boosts.",
    },
    {
      title: "De-essing",
      description:
        "De-essing controls harsh S sounds and sharp consonants. This lets you add brightness or air without making the vocal painful.",
    },
    {
      title: "Reverb and delay",
      description:
        "Space effects help create size and depth. The trick is to make the vocal feel larger without pushing it behind the track.",
    },
  ];

  const vocalProblems = [
    {
      title: "The vocal sounds thin",
      description:
        "Check whether the vocal has enough low-mid body, whether it is too bright, and whether saturation could add useful harmonics. Also make sure it is not simply too quiet.",
    },
    {
      title: "The vocal sounds muddy",
      description:
        "Too much low-mid buildup, room tone, or reverb can make a vocal feel cloudy. Cut the problem areas before adding more presence.",
    },
    {
      title: "The vocal sounds harsh",
      description:
        "Upper-mid buildup, sibilance, or aggressive compression can make a vocal feel sharp. Control harshness before adding top-end brightness.",
    },
    {
      title: "The vocal disappears in the chorus",
      description:
        "The arrangement may be covering it. Use automation, EQ space, compression, saturation, or a slightly different vocal effect balance in louder sections.",
    },
    {
      title: "The vocal sounds small even when loud",
      description:
        "A small vocal may need density, ambience, doubling, parallel processing, or better low-mid support rather than just more volume.",
    },
    {
      title: "The vocal sounds disconnected",
      description:
        "A dry vocal can sit on top of the mix instead of inside it. Subtle reverb, delay, or shared ambience can help it feel connected.",
    },
  ];

  const mistakes = [
    "Boosting low mids too much and making the vocal muddy instead of full.",
    "Adding air before fixing harshness, sibilance, or uneven levels.",
    "Using heavy compression to solve a problem that should be handled with clip gain or automation first.",
    "Making the vocal louder instead of giving it the right tone, density, and space.",
    "Using too much reverb and pushing the vocal away from the listener.",
    "Judging the vocal in solo instead of making decisions with the full mix playing.",
  ];

  const workflowTips = [
    {
      title: "Start with the raw level",
      description:
        "Before EQ or compression, listen to whether the vocal is sitting at a believable level. If some words vanish, fix that first.",
    },
    {
      title: "Remove what does not help",
      description:
        "Clean up rumble, plosives, resonances, and low-mid clutter. A full vocal still needs space around it.",
    },
    {
      title: "Shape body before brightness",
      description:
        "A thin vocal often needs better body, not just more top end. Look for warmth and support before adding air.",
    },
    {
      title: "Add harmonic density",
      description:
        "Use saturation or harmonic enhancement to bring out detail, thickness, and presence without forcing the vocal with EQ.",
    },
    {
      title: "Finish with space and automation",
      description:
        "Use reverb, delay, and subtle automation to keep the vocal emotionally connected throughout the song.",
    },
  ];

  const relatedLinks = [
    { label: "How to Make Vocals Sit in the Mix", to: "/learning/make-vocals-sit-in-mix" },
    { label: "Vocal Processing Chain Explained", to: "/learning/vocal-processing-chain" },
    { label: "What Is Harmonic Saturation?", to: "/learning/harmonic-saturation" },
    { label: "Compression", to: "/learning/compression" },
    { label: "De-Esser", to: "/learning/de-esser" },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const innerPanelSx = {
    borderRadius: "16px",
    backgroundColor: style.darkPanel,
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "none",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="How to Make Vocals Sound Fuller | Parish Audio Learning"
        description="Learn how to make vocals sound fuller with better leveling, EQ, compression, saturation, de-essing, reverb, delay, and practical vocal mixing decisions."
        canonical="https://parishaudio.com/learning/how-to-make-vocals-sound-fuller"
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        imageAlt="Recording studio background for a Parish Audio article about making vocals sound fuller"
      />

      <ArticleSchemaMarkup
        canonical="https://parishaudio.com/learning/how-to-make-vocals-sound-fuller"
        headline="How to Make Vocals Sound Fuller"
        description="Learn how to make vocals sound fuller with better leveling, EQ, compression, saturation, de-essing, reverb, delay, and practical vocal mixing decisions."
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        articleSection="Audio Education"
        datePublished="2026-05-26T00:00:00-06:00"
        dateModified="2026-05-26T00:00:00-06:00"
        keywords={[
          "how to make vocals sound fuller",
          "fuller vocals",
          "vocal mixing",
          "vocal saturation",
          "vocal compression",
          "vocal EQ",
          "how to make vocals sound bigger",
          "music production",
          "mixing vocals",
          "vocal processing",
        ]}
        breadcrumbs={[
          { name: "Home", item: "https://parishaudio.com/" },
          { name: "Learning", item: "https://parishaudio.com/learning" },
          {
            name: "How to Make Vocals Sound Fuller",
            item: "https://parishaudio.com/learning/how-to-make-vocals-sound-fuller",
          },
        ]}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 280, md: 340 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.86) 0%, rgba(8,12,18,0.75) 38%, rgba(8,12,18,0.46) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 820 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Vocal Mixing Guide"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.15rem", md: "3.4rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      How to Make Vocals Sound Fuller
                    </Typography>

                    <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                      May 26, 2026
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 760,
                      }}
                    >
                      A full vocal is not just a loud vocal. Fullness comes from steady
                      level, body, harmonic detail, controlled brightness, and the right
                      amount of space. The goal is to make the vocal feel present and
                      finished without making it muddy, harsh, or overprocessed.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What Makes a Vocal Sound Full?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Vocal fullness is a combination of tone, level, density, and space.
                    A vocal can be bright and still sound thin. It can be loud and still
                    feel small. It can have plenty of low mids and still disappear if the
                    level is uneven or the arrangement is covering it.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    The most common mistake is trying to make a vocal fuller with one big
                    move. In reality, fuller vocals usually come from several smaller
                    decisions working together: clean recording, stable gain, good EQ,
                    controlled dynamics, harmonic enhancement, de-essing, and tasteful
                    ambience.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    The goal is not to make the vocal huge in solo. The goal is to make it
                    feel confident in the track. A vocal that sounds slightly lean by
                    itself may be perfect in a dense mix, while a vocal that sounds rich in
                    solo may become cloudy once the instruments come in.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GraphicEqRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Important Vocal Frequency Areas
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    These ranges are starting points, not rules. Every singer, microphone,
                    room, arrangement, and song will shift the exact frequency choices.
                    Listen in context and make small moves before reaching for extreme EQ.
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {fullnessAreas.map((area) => (
                      <Paper key={area.range} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack spacing={0.8}>
                          <Chip
                            label={area.range}
                            size="small"
                            sx={{
                              alignSelf: "flex-start",
                              borderRadius: "5px",
                              color: style.color4,
                              backgroundColor: "rgba(255,255,255,0.06)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              fontWeight: 700,
                            }}
                          />

                          <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                            {area.title}
                          </Typography>

                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {area.description}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Step-by-Step: How to Build a Fuller Vocal
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {techniques.map((step, index) => (
                        <Paper
                          key={step.title}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Stack spacing={0.6}>
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {index + 1}. {step.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: style.color3, lineHeight: 1.65 }}
                            >
                              {step.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Common Mistakes
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.25, lineHeight: 1.75 },
                      }}
                    >
                      {mistakes.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <HeadphonesRoundedIcon sx={{ color: accent }} />
                          <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                            Quick listening test
                          </Typography>
                        </Stack>

                        <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                          Turn the mix down quietly. If the vocal still feels present,
                          steady, and understandable without jumping out harshly, the
                          fullness is probably coming from balance and tone rather than
                          just volume.
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompressRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Tools That Help Vocals Sound Fuller
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    No single plugin type solves every vocal problem. Fuller vocals usually
                    come from a chain where each processor does a specific job. The exact
                    order can change, but the purpose of each stage should be clear.
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {processingTools.map((tool) => (
                      <Paper key={tool.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack spacing={0.8}>
                          <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                            {tool.title}
                          </Typography>

                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {tool.description}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Why Saturation Helps Fuller Vocals
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Saturation is useful because it can add harmonic detail that helps a
                      vocal feel more present without simply making it brighter or louder.
                      This is especially helpful when a vocal feels dull, small, or too
                      clean in a dense mix.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Instead of only boosting EQ, saturation can create or emphasize
                      overtones that make the vocal easier to hear. This can add density,
                      edge, warmth, or forwardness depending on the type of saturation and
                      how hard it is driven.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Used carefully, saturation can make vocals feel more finished while
                      still keeping them natural. Used too heavily, it can exaggerate
                      harshness, sibilance, or low-mid buildup.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WavesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Space Makes Vocals Feel Bigger
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      A vocal can feel small if it is completely dry, even when the tone is
                      good. Reverb and delay give the vocal a sense of size, depth, and
                      placement. The trick is to add space without losing intimacy.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Short plates, small rooms, slap delays, stereo delays, and quiet
                      ambience can all make a vocal feel larger. Filtering the reverb and
                      delay returns keeps the space from crowding the low mids or creating
                      harsh top-end buildup.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      If the vocal gets washed out, shorten the decay, reduce the send,
                      add pre-delay, or use delay instead of more reverb.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Diagnosing Common Vocal Fullness Problems
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    When a vocal does not feel full, avoid guessing. Listen for the actual
                    problem first. A thin vocal, muddy vocal, harsh vocal, and inconsistent
                    vocal all need different fixes.
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {vocalProblems.map((problem) => (
                      <Paper key={problem.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack spacing={0.8}>
                          <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                            {problem.title}
                          </Typography>

                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {problem.description}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Practical Vocal Fullness Workflow
                    </Typography>
                  </Stack>

                  <Stack spacing={1.2}>
                    {workflowTips.map((step, index) => (
                      <Paper
                        key={step.title}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Stack spacing={0.6}>
                          <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                            {index + 1}. {step.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {step.description}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Example: A Thin Vocal in a Dense Mix
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      If a vocal feels thin once the full arrangement comes in, do not
                      immediately reach for a big high-shelf boost. First, make sure the
                      vocal level is steady. Then clean up rumble, shape body, and check
                      whether guitars, synths, or cymbals are masking the vocal.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      After the basic balance is working, harmonic enhancement can help the
                      vocal feel more detailed and solid without forcing it to become
                      harsh. Auto leveling can keep the vocal steady, while saturation or
                      harmonic enhancement can add density and presence.
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        component={RouterLink}
                        to="/products/he1-harmonic-enhancer"
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: style.accentGreen,
                          "&:hover": {
                            backgroundColor: style.accentGreen,
                            filter: "brightness(1.05)",
                          },
                        }}
                      >
                        View HE1
                      </Button>

                      <Button
                        component={RouterLink}
                        to="/products/auto-level"
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: `1px solid ${accent}`,
                          "&:hover": {
                            borderColor: style.color4,
                            backgroundColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        View Auto Level
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MenuBookRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Related Topics
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                      These topics connect directly to fuller vocals, vocal tone, vocal
                      level control, harmonic density, and vocal processing chains.
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {relatedLinks.map((link) => (
                        <Button
                          key={link.to}
                          component={RouterLink}
                          to={link.to}
                          sx={{
                            borderRadius: "5px",
                            textTransform: "none",
                            fontWeight: 700,
                            color: style.color4,
                            backgroundColor: "rgba(255,255,255,0.05)",
                            border: `1px solid ${accent}`,
                            "&:hover": {
                              borderColor: style.color4,
                              backgroundColor: "rgba(255,255,255,0.10)",
                            },
                          }}
                        >
                          {link.label}
                        </Button>
                      ))}
                    </Stack>

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Fuller vocals come from balance first, processing second. When the
                      level is steady, the body is controlled, the harshness is managed,
                      and the space is intentional, the vocal can sound bigger without
                      needing to be forced.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>
          </Stack>
        </Box>
      </Box>
    </>
  );
}